/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState, useCallback } from "react";
import { Card, Text, BlockStack, Button, Modal, Pagination, Icon } from "@shopify/polaris";
import { ExportIcon, FileIcon } from "@shopify/polaris-icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { exportOrdersByFile } from "../../store/store/slices/Payment";
import { showToast } from "../../store/store/slices/AlertSlice";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

dayjs.extend(relativeTime);

type UploadHistoryItem = {
    _id: string;
    file_name: string;
    uploaded: boolean;
    store: string;
    processed_orders?: number;
    createdAt: string;
    updatedAt: string;
    __v: number;
};

type PaginationData = {
    total: number;
    page: number;
    limit: number;
    pages: number;
};

type UploadHistoryTimelineProps = {
    history: UploadHistoryItem[];
    isLoading?: boolean;
    pagination?: PaginationData;
};

const UploadHistoryTimeline = ({ history, isLoading, pagination }: UploadHistoryTimelineProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [exportingFileId, setExportingFileId] = useState<string | null>(null);
    const [modalActive, setModalActive] = useState(false);
    const [exportMessage, setExportMessage] = useState<string>("");
    const [confirmationModalActive, setConfirmationModalActive] = useState(false);
    const [fileIdToExport, setFileIdToExport] = useState<string | null>(null);
    const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");


    const handlePageChange = useCallback((page: number) => {
        const params = new URLSearchParams(location.search);
        params.set("page", String(page));
        navigate({ search: params.toString() });
    }, [location.search, navigate]);

    const handleExportClick = useCallback((fileId: string) => {
        setFileIdToExport(fileId);
        setConfirmationModalActive(true);
    }, []);

    const handleConfirmExport = useCallback(async () => {
        if (!fileIdToExport) return;
        
        setConfirmationModalActive(false);
        setExportingFileId(fileIdToExport);
        try {
            const response = await exportOrdersByFile(fileIdToExport);
            setExportMessage(response.message || "You will receive an email whenever your data is ready");
            setModalActive(true);
        } catch (error) {
            dispatch(
                showToast({
                    message: "Failed to export orders",
                    error: true,
                    visible: true,
                })
            );
        } finally {
            setExportingFileId(null);
            setFileIdToExport(null);
        }
    }, [fileIdToExport, dispatch]);

    const handleCancelExport = useCallback(() => {
        setConfirmationModalActive(false);
        setFileIdToExport(null);
    }, []);

    const formattedHistory = useMemo(() => {
        if (!history || history.length === 0) return [];

        return history.map((item) => {
            const date = dayjs(item.createdAt);
            return {
                ...item,
                formattedDate: date.format("MMM D, YYYY"),
                formattedTime: date.format("h:mm A"),
                relativeTime: date.fromNow(),
                fullDate: date.format("MMMM D, YYYY [at] h:mm A"),
            };
        });
    }, [history]);

    if (isLoading) {
        return (
            <Card>
                <BlockStack gap="400">
                    <div
                        style={{
                            padding: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Text tone="subdued" as="p">
                            Loading history...
                        </Text>
                    </div>
                </BlockStack>
            </Card>
        );
    }

    if (!history || history.length === 0) {
        return (
            <Card>
                <BlockStack gap="400">
                    <div
                        style={{
                            padding: "20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Text tone="subdued" as="p">
                            No upload history available
                        </Text>
                    </div>
                </BlockStack>
            </Card>
        );
    }

    return (
        <>
            <Modal
                open={confirmationModalActive}
                onClose={handleCancelExport}
                title="Confirm Export"
                primaryAction={{
                    content: "Yes, Export",
                    onAction: handleConfirmExport,
                    
                }}
                secondaryActions={[
                    {
                        content: "Cancel",
                        onAction: handleCancelExport,
                    },
                ]}
            >
                <Modal.Section>
                    <Text as="p" variant="bodyMd">
                        Are you sure you want to export orders marked as paid from this file???
                    </Text>
                </Modal.Section>
            </Modal>
            <Modal
                open={modalActive}
                onClose={() => setModalActive(false)}
                title="Export Orders"
                primaryAction={{
                    content: "OK",
                    onAction: () => setModalActive(false),
                }}
            >
                <Modal.Section>
                    <Text as="p" variant="bodyMd">
                        {exportMessage}
                    </Text>
                </Modal.Section>
            </Modal>
            <Card>
                <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">
                        Upload History
                    </Text>
                    <div
                        style={{
                            position: "relative",
                            paddingLeft: "32px",
                            paddingTop: "8px",
                        }}
                    >
                        {/* Vertical timeline line */}
                        <div
                            style={{
                                position: "absolute",
                                left: "11px",
                                top: "8px",
                                bottom: "8px",
                                width: "1px",
                                //   backgroundColor: "#e1e3e5",
                                borderRight: "2px dotted #e1e3e5"
                            }}
                        />

                        {/* Timeline events */}
                        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                            {formattedHistory.map((item) => (
                                <div
                                    key={item._id}
                                    style={{
                                        position: "relative",
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "8px",
                                    }}
                                >
                                    {/* Timeline dot */}
                                    <div
                                        style={{
                                            position: "absolute",
                                            left: "-26px",
                                            top: "2px",
                                            width: "12px",
                                            height: "12px",
                                            borderRadius: "50%",
                                            backgroundColor: item.uploaded ? "#008060" : "#d72c0d",
                                            border: "2px solid #ffffff",
                                            boxShadow: "0 0 0 2px #e1e3e5",
                                            zIndex: 1,
                                        }}
                                    />

                                    {/* Event content */}
                                    <div style={{ flex: 1 }}>
                                        {/* Time */}
                                        <Text variant="bodySm" tone="subdued" as="p" fontWeight="medium">
                                            {item.formattedTime} - {item.formattedDate}
                                        </Text>

                                        {/* Event description */}
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: "4px",
                                                marginTop: "4px",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: "12px",
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <div style={{ flex: "1", minWidth: "200px", display: "flex", alignItems: "center",justifyContent: "start", gap: "8px" }}>
                                                    <div>

                                                    <Icon source={FileIcon} tone="success" />
                                                    </div>
                                                    <Text variant="bodyMd" as="p">
                                                        {item.uploaded ? "Successfully uploaded" : "Upload failed"}{" "}
                                                        <strong>{item.file_name}</strong>
                                                    </Text>
                                                </div>
                                                {item.uploaded && (
                                                    <Button
                                                        size="micro"
                                                        variant={theme === "dark" ? "secondary" : "primary"}
                                                        icon={ExportIcon}
                                                        onClick={() => handleExportClick(item._id)}
                                                        loading={exportingFileId === item._id}
                                                        disabled={exportingFileId !== null || (item.processed_orders ?? 0) === 0}
                                                    >
                                                        Export
                                                    </Button>
                                                )}
                                            </div>
                                            {item.uploaded && (
                                                <Text variant="bodySm" tone="subdued" as="p">
                                                    File processed and payment statuses updated
                                                    {item.processed_orders !== undefined && item.processed_orders !== null && (
                                                        <span> • <strong>{item.processed_orders}</strong> {item.processed_orders === 1 ? 'order' : 'orders'} marked as paid</span>
                                                    )}
                                                </Text>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {pagination && pagination.pages > 1 && (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                paddingTop: "24px",
                            }}
                        >
                            <Pagination
                                onPrevious={() => {
                                    if (pagination.page > 1) {
                                        handlePageChange(pagination.page - 1);
                                    }
                                }}
                                onNext={() => {
                                    if (pagination.page < pagination.pages) {
                                        handlePageChange(pagination.page + 1);
                                    }
                                }}
                                hasPrevious={pagination.page > 1}
                                hasNext={pagination.page < pagination.pages}
                                label={`Page ${pagination.page} of ${pagination.pages}`}
                            />
                        </div>
                    )}
                </BlockStack>
            </Card>
        </>
    );
};

export default UploadHistoryTimeline;

